import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
} from 'react'
import PropTypes from 'prop-types'
import { useForm, Controller } from 'react-hook-form'
import * as yup from 'yup'
import { yupResolver } from '@hookform/resolvers'
import {
  Box,
  Button,
  Card,
  CardContent,
  TextField,
  Typography,
} from '@material-ui/core'
import ToggleButtonGroup from '@material-ui/lab/ToggleButtonGroup'
import SegmentedToggleButton from 'components/segmented-toggle-button'
import CheckIcon from 'components/material-symbol/icons/Check'
import GroupIcon from 'components/material-symbol/icons/Group'
import LockIcon from 'components/material-symbol/icons/Lock'
import { useNoteComposerStyles } from './styles'

const schema = yup.object().shape({
  title: yup.string().nullable(),
  content: yup
    .string()
    .trim()
    .required('Content is required'),
  isPrivate: yup.boolean().required(),
})

const defaultValues = {
  title: '',
  content: '',
  isPrivate: true,
}

const PRIVACY_GROUP_STYLE = {
  overflow: 'visible',
  display: 'inline-flex',
  flexWrap: 'nowrap',
  width: 'max-content',
  maxWidth: 'none',
  minWidth: 'max-content',
}

const PRIVACY_BUTTON_STYLE = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  flex: '0 0 auto',
  flexGrow: 0,
  flexShrink: 0,
  width: 'auto',
  minWidth: 'max-content',
  maxWidth: 'none',
  overflow: 'visible',
}

export function noteContentPlaceholder(firstName) {
  if (firstName) {
    return `Notes about ${firstName}'s lessons, goals, swing changes...`
  }
  return "Notes about this student's lessons, goals, swing changes..."
}

function PrivacyOptionLabel({ icon: Icon, label }) {
  const classes = useNoteComposerStyles()

  return (
    <span className={classes.privacyLabel}>
      <Icon fontSize="small" />
      {label}
    </span>
  )
}

PrivacyOptionLabel.propTypes = {
  icon: PropTypes.elementType.isRequired,
  label: PropTypes.string.isRequired,
}

const NoteComposer = forwardRef(function NoteComposer(
  {
    heading,
    mode,
    isEditing,
    note,
    studentFirstName,
    defaultTitle,
    onSubmit,
    onCancel,
    isSubmitting,
    embedded,
  },
  ref,
) {
  const classes = useNoteComposerStyles()
  const titleInputRef = useRef(null)
  const { register, handleSubmit, reset, control, watch, errors } = useForm({
    resolver: yupResolver(schema),
    defaultValues,
  })
  const isPrivate = watch('isPrivate', true)
  const editing = isEditing === true || mode === 'edit'

  useEffect(() => {
    if (editing && note) {
      reset({
        title: note.title ?? '',
        content: note.content ?? '',
        isPrivate: note.private ?? true,
      })
      return
    }

    reset({
      ...defaultValues,
      title: defaultTitle ?? '',
    })
  }, [editing, note, defaultTitle, reset])

  useImperativeHandle(ref, () => ({
    focusTitle: () => titleInputRef.current?.focus(),
  }))

  const handleSecondaryAction = () => {
    if (editing) {
      onCancel?.()
      return
    }
    reset({
      ...defaultValues,
      title: defaultTitle ?? '',
    })
  }

  const onFormSubmit = async data => {
    try {
      await onSubmit({
        title: data.title?.trim() || null,
        content: data.content.trim(),
        isPrivate: data.isPrivate,
      })
    } catch {
      return
    }
    if (!editing) {
      reset({
        ...defaultValues,
        title: defaultTitle ?? '',
      })
    }
  }

  const headingLabel = heading || (editing ? 'Edit note' : 'Add a note')

  const form = (
    <>
      {heading !== null && (
        <Typography className={classes.heading} component="h2">
          {headingLabel}
        </Typography>
      )}
      <form className={classes.form} onSubmit={handleSubmit(onFormSubmit)}>
        <TextField
          name="title"
          placeholder="Note title"
          fullWidth
          variant="outlined"
          className={classes.formField}
          inputRef={element => {
            register(element)
            titleInputRef.current = element
          }}
          inputProps={{ maxLength: 255, 'aria-label': 'Note title' }}
        />
        <TextField
          name="content"
          placeholder={noteContentPlaceholder(studentFirstName)}
          fullWidth
          multiline
          minRows={3}
          maxRows={10}
          variant="outlined"
          className={`${classes.formField} ${classes.contentField}`}
          inputRef={register}
          error={!!errors.content}
          helperText={errors.content?.message}
          inputProps={{ 'aria-label': 'Note content' }}
          required
        />
        <Box className={classes.privacySection}>
          <Controller
            name="isPrivate"
            control={control}
            defaultValue
            render={({ value, onChange }) => {
              const groupValue = value ? 'private' : 'shared'
              return (
                <ToggleButtonGroup
                  exclusive
                  value={groupValue}
                  onChange={(_, nextValue) => {
                    if (!nextValue) return
                    onChange(nextValue === 'private')
                  }}
                  aria-label="Who can see this note?"
                  className={classes.privacyGroup}
                  style={PRIVACY_GROUP_STYLE}
                >
                  <SegmentedToggleButton
                    type="button"
                    value="private"
                    groupValue={groupValue}
                    className={classes.privacyButton}
                    style={PRIVACY_BUTTON_STYLE}
                  >
                    <PrivacyOptionLabel icon={LockIcon} label="Private" />
                  </SegmentedToggleButton>
                  <SegmentedToggleButton
                    type="button"
                    value="shared"
                    groupValue={groupValue}
                    className={classes.privacyButton}
                    style={PRIVACY_BUTTON_STYLE}
                  >
                    <PrivacyOptionLabel icon={GroupIcon} label="Share" />
                  </SegmentedToggleButton>
                </ToggleButtonGroup>
              )
            }}
          />
          <Typography className={classes.helper}>
            {isPrivate
              ? 'Only you can see this note.'
              : 'Your student can see this note.'}
          </Typography>
        </Box>
        <Box className={classes.actions}>
          <Button onClick={handleSecondaryAction} disabled={isSubmitting}>
            {editing ? 'Cancel' : 'Clear'}
          </Button>
          <Button
            type="submit"
            variant="contained"
            color="primary"
            disableElevation
            className={classes.saveButton}
            startIcon={<CheckIcon fontSize="small" />}
            disabled={isSubmitting}
          >
            Save note
          </Button>
        </Box>
      </form>
    </>
  )

  if (embedded) {
    return <Box className={classes.embedded}>{form}</Box>
  }

  return (
    <Card className={classes.card} variant="outlined">
      <CardContent className={classes.cardContent}>{form}</CardContent>
    </Card>
  )
})

NoteComposer.propTypes = {
  heading: PropTypes.oneOfType([PropTypes.string]),
  mode: PropTypes.oneOf(['create', 'edit']).isRequired,
  isEditing: PropTypes.bool,
  note: PropTypes.shape({
    id: PropTypes.string,
    title: PropTypes.string,
    content: PropTypes.string,
    private: PropTypes.bool,
  }),
  studentFirstName: PropTypes.string,
  defaultTitle: PropTypes.string,
  onSubmit: PropTypes.func.isRequired,
  onCancel: PropTypes.func,
  isSubmitting: PropTypes.bool,
  embedded: PropTypes.bool,
}

NoteComposer.defaultProps = {
  heading: undefined,
  isEditing: false,
  note: null,
  studentFirstName: null,
  defaultTitle: '',
  onCancel: null,
  isSubmitting: false,
  embedded: false,
}

export default NoteComposer
